import { submissionStatusLabel } from '../../../constants/submissionStatus';
import { useTranslation } from 'react-i18next';
import React from 'react';
import Pagination, { usePagination } from '../../../components/ui/Pagination';
import EmptyState from '../../../components/ui/EmptyState';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { formatDate } from '../../../utils/formatters';
import SortableTh, { useSort } from '../../../components/ui/SortableTh';
import useTableFilters from '../../../components/ui/useTableFilters';

const SubmissionTable = ({ submissions, onViewSubmission, filtered = false, onClearFilters }) => {
  const { t } = useTranslation('common');
  // Sorting is table-local: this component owns the rows it renders, so the
  // parent doesn't need to know or care that they can be reordered.
  const { sort, toggle, sortRows } = useSort('submittedAt', 'desc');
  const f = useTableFilters(submissions, React.useMemo(() => ({
    author: { label: 'Author', accessor: (s) => s?.author?.name || s?.authorName || '', emptyLabel: 'No author' },
    track:  { label: 'Track',  accessor: (s) => s?.track || s?.trackName || '', emptyLabel: 'No track' },
    status: { label: 'Status', accessor: (s) => s?.status || '',
      order: ['draft','submitted','under_review','revision_required','accepted','rejected','withdrawn'],
      format: (v) => String(v).replace(/_/g,' ').replace(/^./, (c) => c.toUpperCase()) },
  }), []));
  const SORT_ACCESSORS = {
    author: (r) => `${r?.author?.lastName || ''} ${r?.author?.firstName || ''}`.trim() || r?.authorName || '',
    track: (r) => r?.trackName || r?.track || '',
  };
  const rows = sortRows(f.filterRows(submissions), SORT_ACCESSORS);
  // Paginate after sorting, so page 1 is the top of the sorted list
  // rather than the top of an arbitrary slice.
  const { pageRows, ...pager } = usePagination(rows, 25);
  const getStatusBadgeColor = (status) => {
    const colors = {
      'draft': 'bg-muted text-foreground',
      'submitted': 'bg-primary-subtle text-primary',
      'under_review': 'bg-warning-subtle text-warning',
      'accepted': 'bg-success-subtle text-success',
      'rejected': 'bg-error-subtle text-error',
      'revision_required': 'bg-accent-subtle text-accent'
    };
    return colors?.[status] || 'bg-muted text-foreground';
  };

  if (!submissions || submissions?.length === 0) {
    return (
      <EmptyState
        filtered={filtered}
        onClear={onClearFilters}
        icon="FileText"
        title={filtered ? 'No submissions match these filters' : 'No submissions yet'}
        body={
          filtered
            ? 'Try a broader search, or clear the filters to see every paper.'
            : 'Papers appear here as soon as authors submit to this conference.'
        }
      />
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="table-clear">
        <thead>
          <tr>
            <SortableTh field="title" sort={sort} onSort={toggle} className="col-primary">Title</SortableTh>
            <SortableTh field="author" sort={sort} onSort={toggle} filter={f.headerProps('author')}>Author</SortableTh>
            <SortableTh field="track" sort={sort} onSort={toggle} filter={f.headerProps('track')}>Track</SortableTh>
            <SortableTh field="status" sort={sort} onSort={toggle} filter={f.headerProps('status')}>Status</SortableTh>
            <SortableTh field="submittedAt" sort={sort} onSort={toggle} align="end">Submitted</SortableTh>
            <th className="col-tight">Actions</th>
          </tr>
        </thead>
        <tbody>
          {pageRows?.map((submission) => (
            <tr key={submission?.id}>
              <td>
                <p className="font-medium text-foreground line-clamp-2">{submission?.title}</p>
              </td>
              <td>
                <p className="text-foreground">
                  {submission?.author?.firstName} {submission?.author?.lastName}
                </p>
                <p className="text-xs text-muted-foreground">{submission?.author?.email}</p>
              </td>
              <td>
                <span className="text-foreground">{submission?.trackName || 'N/A'}</span>
              </td>
              <td>
                <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-medium ${getStatusBadgeColor(submission?.status)}`}>
                  {submissionStatusLabel(submission?.status, t)}
                </span>
              </td>
              <td>
                <span className="text-muted-foreground">{formatDate(submission?.submittedAt)}</span>
              </td>
              <td>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onViewSubmission(submission)}
                  iconName="Eye"
                  iconPosition="left"
                >
                  View
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Pagination {...pager} />
    </div>
  );
};

export default SubmissionTable;